import { Injectable } from '@angular/core';
import { NGXLogger as LoggerService } from "ngx-logger";
import { OptionData, OptionListPersistence, OptionPersistence } from 'whocan-lib';
import { AuthService } from '../../../user/auth.service';
import { EssErrorService } from 'ngx-essentia';
import { BehaviorSubject, Observable, Subject, Subscription, firstValueFrom, map } from 'rxjs';
import { skip } from 'rxjs/operators';
import { AngularFireDatabase, AngularFireObject } from '@angular/fire/compat/database';
import { PathService } from '../../core/path.service';
import { OptionClass, OptionList, normalizeOptionPersistence } from 'whocan-lib';
import firebase from 'firebase/compat/app';
import { HeaderService } from '../poll-header/header.service';
import { CurrentPollService } from '../../core/current-poll.service';
import { UserService } from '../../../user/user.service';
import { ConnectionStatusService } from '../../../shared/services/connection-status.service';
import { TranslationGeneralService } from '../../../shared/translation/translation.general.service';
import { isPermissionDenied } from '../../../shared/services/db-errors';
import { AccessGrantService } from '../../core/access-grant.service';

/**
 * What the rules of `whoo/options` decide on, read from the client state at the moment of the write
 * (workorder-auth-window-option-access-link-2026-10-04, tasks 4 and 6). Booleans and numbers only, no
 * uids - the fields go into the error log behind the fixed prefix.
 */
export interface OptionWriteDiagnostics {
    /** auth uid === poll owner */
    authIsOwner: boolean;
    /** the id token carries the delegation of the poll owner */
    tokenHasDelegation: boolean;
    /** the own user record stores a delegation of the poll owner (claim may still be missing) */
    recordHasDelegation: boolean;
    /** the option was created by the signed-in user (participant option) */
    createdBySelf: boolean;
    /** CurrentPollService.isOwner at the time of the write - stale when true while the two token fields are false */
    isOwnerSnapshot: boolean;
    /** the Auth SDK's currentUser is the uid the AuthService holds */
    currentUserMatches: boolean;
    msSinceAuth: number | null;
    tokenIssuedAt: string | null;
    connected: boolean | null;
    online: boolean | null;
}

@Injectable({
    providedIn: 'root'
})

export class OptionsService {
    private _optionList: OptionList
    private _realtimeSubscription: Subscription | null = null;
    private _optionsUpdated$ = new Subject<void>();
    private _editModeCounter: number = 0;
    public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    /**
     * Fires whenever the option list was replaced by a realtime update from the database.
     * Consumers that render options (vote matrix, result matrix) rebuild their columns on it.
     */
    get optionsUpdated$(): Observable<void> {
        return this._optionsUpdated$.asObservable();
    }
    constructor(
        private logger: LoggerService,
        private authService: AuthService,
        private errorService: EssErrorService,
        private afd: AngularFireDatabase,
        private pathService: PathService,
        private headerService: HeaderService,
        private currentPollService: CurrentPollService,
        private userService: UserService,
        private connectionStatus: ConnectionStatusService,
        private trans: TranslationGeneralService,
        private accessGrantService: AccessGrantService,
    ) {
        this._optionList = new OptionList([], this.headerService.headerClass.timeZone)
    }

    public async load(ownerId?: string, pollId?: string) {
        this.isLoading$.next(true);
        try {
            let optionsData: OptionListPersistence = await (firstValueFrom(this.getObservable(ownerId, pollId)))
            this._optionList = new OptionList(optionsData, this.headerService.headerClass.timeZone)
            this.logger.log('load optionsData: ', optionsData)
            this.logger.log('load optionlist: ', this._optionList)
            if (this._optionList.wasHealed && this.currentPollService.isOwner) {
                // the constructor only repaired the sortMap in-memory - write it back
                // so other clients don't run into the same corruption again
                this.logger.warn('load: optionList was healed, persisting repaired sortMap');
                await this.persistSortMap(true);
            }
        } finally {
            this.isLoading$.next(false);
        }
    }

    public uninitialize() {
        this.unsubscribeFromRealtimeUpdates();
        this._editModeCounter = 0;
        this._optionList.uninitialize()
        this.logger.log('uninitialize: load')
        this._optionList = new OptionList([], this.headerService.headerClass.timeZone)
    }

    public get optionList(): OptionList {
        return this._optionList
    }

    /**
     * true if the local option list holds changes (options or sortMap) that were
     * not persisted yet. Used to decide whether the list may be safely re-read
     * from the database (a reload would discard unsaved changes).
     */
    public get hasUnsavedChanges(): boolean {
        if (this._optionList.sortMapHasChangedSinceLastSave) return true
        let returnValue = false
        this._optionList.forEachIncludingDeleted(optionClass => {
            if (this._optionList.optionHasChangedSinceLastSave(optionClass.index)) {
                returnValue = true
            }
        })
        return returnValue
    }

    public set optionList(value: OptionList) {
        this._optionList = value
    }

    /**
     * true while an option is being edited outside of the option list itself (option dialog,
     * multi edit, repeat). The dialogs work on a deep copy, so the list is not dirty yet -
     * a realtime update would replace the OptionClass the copy is written back to and the
     * edit would silently be lost.
     */
    public get isEditModeActive(): boolean {
        return this._editModeCounter > 0;
    }

    public enterEditMode(): void {
        this._editModeCounter++;
    }

    public leaveEditMode(): void {
        if (this._editModeCounter > 0) this._editModeCounter--;
    }

    /**
     * Realtime updates for the option list, mirroring VotesService.subscribeToRealtimeUpdates:
     * the first emission carries the value load() has already read, so it is skipped.
     */
    public subscribeToRealtimeUpdates(ownerId?: string, pollId?: string): void {
        this.unsubscribeFromRealtimeUpdates();
        this._realtimeSubscription = this.getObservable(ownerId, pollId).pipe(
            skip(1) // skip first emission since we already loaded via load()
        ).subscribe({
            next: optionsData => this.applyRealtimeUpdate(optionsData),
            // Since rules-hardening-1 options need auth != null: a logout (also in another tab) revokes the listen.
            // Without this handler that ends up in the global ErrorHandler as an uncaught error (rules-hardening-1b,
            // auth-window 4). Same pattern as the votePrivate listen in VotesService: the loaded list stays, only the
            // live updates end.
            error: error => {
                if (isPermissionDenied(error)) {
                    this.logger.warn('options listen revoked, the option list is no longer live', error);
                } else {
                    this.logger.error('options realtime subscription failed - ' + error);
                }
                this._realtimeSubscription = null;
            },
        });
    }

    public unsubscribeFromRealtimeUpdates(): void {
        this._realtimeSubscription?.unsubscribe();
        this._realtimeSubscription = null;
    }

    /**
     * Applies a remote option list - but only when it really differs and when no local edit
     * would be thrown away by the replacement. There is deliberately no merge attempt: the
     * local state wins and the remote state is picked up with the next load (eventual
     * consistency), which is what the conflict window of an added option needs.
     */
    private applyRealtimeUpdate(optionsData: OptionListPersistence): void {
        const newOptionList = new OptionList(optionsData, this.headerService.headerClass.timeZone)
        if (OptionsService.getContentSnapshot(newOptionList) === OptionsService.getContentSnapshot(this._optionList)) {
            // our own writes echo back with new server timestamps only - refreshing on those
            // would rebuild the list (and re-trigger a heal persist) on every save
            this.logger.log('realtime options update: content unchanged, ignored');
            return;
        }
        if (this.hasUnsavedChanges || this.isEditModeActive) {
            this.logger.log('realtime options update: discarded, local option changes in progress');
            return;
        }
        this._optionList = newOptionList
        this.logger.log('realtime options update applied, options: ', this._optionList.length);
        if (this._optionList.wasHealed && this.currentPollService.isOwner) {
            // same as in load(): write the repaired sortMap back. The write echoes back as
            // another emission whose list is not healed anymore, so this stays a single write
            this.logger.warn('realtime options update: optionList was healed, persisting repaired sortMap');
            this.persistSortMap(true).catch(error => this.logger.error('realtime heal persist failed', error));
        }
        this._optionsUpdated$.next();
    }

    /**
     * Content fingerprint of an option list: sortMap plus the option data without the
     * attributes that change on every write (timestamps) or that never persist (checked).
     */
    private static getContentSnapshot(optionList: OptionList): string {
        const options: OptionPersistence[] = []
        optionList.forEachIncludingDeleted(optionClass => {
            const optionData: OptionPersistence = optionClass.data
            delete optionData.updatedAt
            delete optionData.createdAt
            delete optionData.checked
            options.push(optionData)
        })
        return JSON.stringify({ sortMap: optionList.sortMap, options })
    }

    getObservable(ownerId?: string, pollId?: string): Observable<OptionListPersistence> {
        this.logger.log('optionsObservable');
        const ref: AngularFireObject<(OptionData[])> = this.afd.object(this.pathService.getOptionsPath(ownerId, pollId));
        return ref.valueChanges().pipe(
            map(data => {
                this.logger.log('obeservable: ', data);
                return data
            })
        );
    }

    /**
     * The one and only method to persist an option.
     * Updates also the optionList with resetLastSaveForOneOption
     * @param optionClass
     */
    public async persistOption(optionClass: OptionClass): Promise<void> {
        // undefined values (multi-edit dialog with an empty, checked field) would make the Firebase
        // client reject the whole write: top-level -> null, nested -> removed
        // (workorder-persist-option-undefined-2026-10-04, step 1)
        const persistenceData: OptionPersistence = normalizeOptionPersistence(optionClass.data)
        this.logger.log('persistOption', persistenceData);
        if (!persistenceData) {
            throw this.errorService.newError('persistOption: option undefined');
        }
        const index = persistenceData.index;
        this.logger.log('persist: ', persistenceData, index);
        if (!persistenceData.createdAt) {
            persistenceData.createdAt = firebase.database.ServerValue.TIMESTAMP;
        }
        persistenceData.updatedAt = firebase.database.ServerValue.TIMESTAMP;;
        // we don't want to persist the "Checked" attribute so we copy the option and remove the checked attribute
        persistenceData.checked = null;
        // a missing index used to land under the literal key "undefined" (133 polls in prod); the rules reject a
        // non-numeric key in the flat structure since rules-hardening-1 - fail here with a readable error instead
        if (!isPersistableIndex(index)) {
            throw this.errorService.newError('persistOption: index missing');
        }
        let path: string;
        if (this._optionList.isLegacyData) {
            path = `${this.pathService.getOptionsPath()}/${index}`
        } else {
            path = `${this.pathService.getOptionsArrayPath()}/${index}`
        }
        // the owner from the poll context - the same value the path is built from
        const ownerId = this.currentPollService.ownerId;
        const pollId = this.currentPollService.pollId;
        await this.preflightOptionWrite(persistenceData, ownerId, pollId);
        const grantRole = this.accessGrantService.hasGrantRole(ownerId, pollId);
        const ref: AngularFireObject<OptionData> = this.afd.object(path);
        try {
            await ref.update(persistenceData);
        } catch (error) {
            if (grantRole && isPermissionDenied(error)) {
                // the write went through a grant and the rules refused it: key rotated or grant removed
                // since the role was resolved - a user state (workorder-poll-scoped-access-1-2, rule 6)
                this.accessGrantService.invalidate(ownerId, pollId);
                this.logger.warn('persistOption: grant no longer valid, write denied', path, error);
                throw error;
            }
            // prefix, path and error stay in front unchanged - the triage register keys on
            // `^persistOption failed.*,Error: PERMISSION_DENIED`; the diagnostics behind them tell the
            // mechanisms apart (task 6)
            this.logger.error('persistOption failed', path, error, this.getWriteDiagnostics(persistenceData, ownerId));
            throw error;
        }
        this.optionList.resetLastSaveForOneOption(optionClass)
    }

    /**
     * Last gate before an option write, mirroring what the rules check (`whoo/options/$pollOwnerId`:
     * owner or delegate of the owner; `optionArray/$optionIndex`: creator of the option or delegate of
     * the creator). Synchronous checks plus at most ONE wait:
     *  - the token allows the write or the option is the participant's own -> write,
     *  - a resolved grant role on the poll (co-owner / editor, workorder 1.2) -> write,
     *  - the record stores the delegation but the token lacks the claim yet (access link just
     *    opened, `setDelegationCustomClaim` still running) -> wait once for the claim, then write,
     *  - neither -> no write at all (stale isOwner after an auth change, claim withdrawn): a readable
     *    message instead of a PERMISSION_DENIED error report.
     * The aborted write is a warning, not an error - the pre-flight prevents one.
     * (workorder-auth-window-option-access-link-2026-10-04, task 4)
     */
    private async preflightOptionWrite(persistenceData: OptionPersistence, ownerId: string, pollId: string): Promise<void> {
        const uid = this.authService.uid;
        if (this.tokenAllowsWrite(uid, ownerId) || this.creatorAllowsWrite(persistenceData, uid)
            || this.accessGrantService.canEditPoll(ownerId, pollId)) {
            return;
        }
        if (this.recordHasDelegation(ownerId)) {
            // mechanism 1: delegation stored, claim not in the token yet. waitForClaims may do one
            // forced refresh at timeout - acceptable here because the record proves the delegation
            this.logger.warn('persistOption: delegation claim not in token yet, waiting');
            const result = await this.authService.waitForClaims(ownerId, 5000);
            if (result.granted) {
                return;
            }
            this.logger.warn('persistOption: delegation claim did not arrive, not writing', result.errorCode ?? 'timeout');
            throw this.errorService.newError(this.trans.translate('noAccess'), false);
        }
        // mechanism 2: no rights (any more) - do not even try
        this.logger.warn('persistOption: no edit rights for this poll, not writing');
        throw this.errorService.newError(this.trans.translate('noAccess'), false);
    }

    /** `$pollOwnerId === auth.uid || auth.token.delegations.contains($pollOwnerId)` */
    private tokenAllowsWrite(uid: string | undefined, ownerId: string): boolean {
        return !!uid && !!ownerId && (uid === ownerId || this.authService.getDelegatedUserIds().includes(ownerId));
    }

    /** `optionArray/$optionIndex`: creator of the option or delegate of the creator - not on the legacy path */
    private creatorAllowsWrite(persistenceData: OptionPersistence, uid: string | undefined): boolean {
        const createdByUid = persistenceData.createdByUid;
        if (this._optionList.isLegacyData || !uid || !createdByUid) {
            return false;
        }
        return createdByUid === uid || this.authService.getDelegatedUserIds().includes(createdByUid);
    }

    private recordHasDelegation(ownerId: string): boolean {
        return !!ownerId && (this.userService.dbUser?.delegationFrom?.some(d => d.delegatedFromUid === ownerId) ?? false);
    }

    /** @see OptionWriteDiagnostics - all synchronous, no round trip */
    private getWriteDiagnostics(persistenceData: OptionPersistence, ownerId: string): OptionWriteDiagnostics {
        const uid = this.authService.uid;
        return {
            authIsOwner: !!uid && uid === ownerId,
            tokenHasDelegation: !!ownerId && this.authService.getDelegatedUserIds().includes(ownerId),
            recordHasDelegation: this.recordHasDelegation(ownerId),
            createdBySelf: !!uid && persistenceData.createdByUid === uid,
            isOwnerSnapshot: this.currentPollService.isOwner,
            currentUserMatches: this.authService.sdkCurrentUserUid === (uid ?? null),
            msSinceAuth: this.authService.msSinceAuthStateChange,
            tokenIssuedAt: this.authService.tokenIssuedAtTime ?? null,
            connected: this.connectionStatus.connected ?? null,
            online: globalThis.navigator?.onLine ?? null,
        };
    }

    /**
     * persistOption + persistSortMap for an option that was just added. With `afterSignUp` (the caller
     * signed the visitor in anonymously a moment ago) a PERMISSION_DENIED gets exactly ONE retry after
     * the SDK reports the connection as established - the first auth-bound write of a fresh session
     * can be rejected before the new token is effective on the connection, same as the header write in
     * PollService.persistNewPoll. No forced token refresh (rate limited), no retry in persistOption
     * itself (workorder-auth-window-option-access-link-2026-10-04, task 5).
     */
    public async persistAddedOption(optionClass: OptionClass, afterSignUp: boolean): Promise<void> {
        try {
            await this.persistOption(optionClass);
        } catch (error) {
            if (!afterSignUp || !isPermissionDenied(error)) {
                throw error;
            }
            this.logger.warn('persistOption: option write denied right after sign-up, retrying once');
            await this.connectionStatus.waitForConnected(3000);
            await new Promise(resolve => setTimeout(resolve, 500));
            await this.persistOption(optionClass);
        }
        await this.persistSortMap();
    }

    /**
     * The one and only place to perist the sortMap of the optionList
     * Only for non legacy data
     * Updates also the sortList with resetSortMapLastSave
     *
     */
    public async persistSortMap(force: boolean = false): Promise<void> {
        if (force || this.optionList.sortMapHasChangedSinceLastSave) {
            if (!this._optionList.isLegacyData) {
                this.logger.log('Persist sortmap')
                const ref: AngularFireObject<number[]> = this.afd.object(`${this.pathService.getOptionsSortMapPath()}`);
                try {
                    await ref.set(this._optionList.sortMap);
                } catch (error) {
                    this.logger.error('persistSortMap failed', error);
                    throw error;
                }
                this.optionList.resetSortMapLastSave()
            } else {
                this.logger.log('Dont persist sortmap its legacy data')
            }
        }
    }

    /**
     * Persists  options in optionList as well as the sortmap
     * If force is not set persists only changed options and sortmap
     * Updates also the optionList with resetLastSave()
     */
    public async persist(force: boolean = false): Promise<boolean> {
        // a poll deleted in this session is never written again - the option writes of the trailing auto-save left
        // options without a header behind (workorder-header-persist-and-poll-init-denied-with-user-2026-11-03, task 0)
        if (this.currentPollService.isCurrentPollDeleted) {
            this.logger.warn('OptionsService.persist: skipped, poll deleted');
            this._optionList.resetLastSave();
            return false;
        }
        let hasChanges = false;
        const persistPromises: Promise<void>[] = [];
        this._optionList.forEachIncludingDeleted(optionClass => {
            if (force || this._optionList.optionHasChangedSinceLastSave(optionClass.index)) {
                this.logger.log('persist optionClass', optionClass)
                persistPromises.push(this.persistOption(optionClass));
                hasChanges = true;
            }
        })
        persistPromises.push(this.persistSortMap(force));
        await Promise.all(persistPromises);
        this._optionList.resetLastSave()
        return hasChanges;
    }

    /**
   * save all options that are created by a the current particpant (owner options are not saved)
   * @param name
   */
    public async saveOptionsCreatedByParticpant(name: string): Promise<void> {
        const persistPromises: Promise<void>[] = [];
        this._optionList.forEach(option => {
            if (option.isCreatedByParticipant && option.createdByUid === this.authService.uid) {
                option.createdByName = name;
                persistPromises.push(this.persistOption(option));
            }
        });
        if (persistPromises.length > 0) {
            // safety net: if the participant's options were never persisted together with
            // the sortMap (e.g. connection loss during add), persist the sortMap now
            persistPromises.push(this.persistSortMap());
        }
        await Promise.all(persistPromises);
    }

    public markAsCreatedByParticpant(option: OptionClass) {
        option.setMarkAsCreatedBy(this.authService.displayName, this.authService.uid)
    }

    public get dateOptions(): OptionClass[] {
        return this._optionList.dateOptions
    }
    public get textOptions(): OptionClass[] {
        return this._optionList.textOptions
    }
    public get allOptions(): OptionClass[] {
        return this._optionList.allOptions
    }

    /**
     * Creates a copy of an OptionList and persists it.
     * Waits
     * @param ownerId - The ID of the current owner.
     * @param pollId - The ID of the current poll.
     * @param newOwnerId - The ID of the new owner.
     * @param newPollId - The ID of the new poll.
     * @returns A promise that resolves to the new OptionList
     */
    public async persistCopyOfOptionList(ownerId: string, pollId: string, newOwnerId: string, newPollId: string, timeZone: string): Promise<OptionList> {
        this.logger.log('persistCopyOfOptionList')
        try {
            let optionsData: OptionListPersistence = await (firstValueFrom(this.getObservable(ownerId, pollId)))
            if (optionsData) {
                const optionList = new OptionList(optionsData, timeZone)
                this.logger.log('persistCopyOfOptionList: optionlist: ', optionList)
                const persistPromises: Promise<void>[] = []
                optionList.forEachIncludingDeleted((optionClass, index) => {
                    persistPromises.push(this.persistCopyOfOption(optionClass, newOwnerId, newPollId, index))
                })
                persistPromises.push(this.persistCopyOfSortMap(optionList, newOwnerId, newPollId))
                await Promise.all(persistPromises);
                return optionList
            } else {
                this.logger.warn('persistCopyOfOptionList: no such options', ownerId, pollId)
                return null
            }
        } catch (exception) {
            this.errorService.newError('persistCopyOfOptionList: ' + exception)
        }
    }



    /**
     * The copy is always written as optionArray/{i} + sortMap, also for a flat legacy source - no new flat
     * option polls (rules-hardening-1b task 3). The source itself is left as it is.
     */
    private persistCopyOfOption(option: OptionClass, newOwnerId: string, newPollId, index: number) {
        this.logger.log('persistCopyOfOption', option);
        if (!option) throw this.errorService.newError('persistCopyOfOption: option undefined');
        if (!isPersistableIndex(index)) throw this.errorService.newError('persistCopyOfOption: index missing');
        option.createdAt = firebase.database.ServerValue.TIMESTAMP;
        option.updatedAt = firebase.database.ServerValue.TIMESTAMP;;
        const path = `${this.pathService.getOptionsArrayPath(newOwnerId, newPollId)}/${index}`
        const ref: AngularFireObject<OptionData> = this.afd.object(path);
        return ref.set(option.data);
    }

    private persistCopyOfSortMap(optionList: OptionList, newOwnerId: string, newPollId: string) {
        this.logger.log('copySortMap sortmap')
        const ref: AngularFireObject<number[]> = this.afd.object(`${this.pathService.getOptionsSortMapPath(newOwnerId, newPollId)}`);
        // the sortMap of the COPIED list - this._optionList is the currently loaded
        // poll, which is not necessarily the one being copied. A flat legacy source has no stored
        // sortMap, OptionList derives it from the indexes
        return ref.set(optionList.sortMap);
    }

    public async deleteOptions(ownerId: string, pollId: string): Promise<void> {
        const ref: AngularFireObject<any> = this.afd.object(this.pathService.getOptionsPath(ownerId, pollId));
        const sortMapRef: AngularFireObject<number[]> = this.afd.object(`${this.pathService.getOptionsSortMapPath(ownerId, pollId)}`);
        await Promise.all([ref.remove(), sortMapRef.remove()]);
    }

}

/** a path segment of an option node must be a whole number - never undefined, null or NaN (rules-hardening-1) */
function isPersistableIndex(index: unknown): index is number {
    return index !== undefined && index !== null && Number.isInteger(index);
}
